/* eslint-disable react-hooks/exhaustive-deps */
// eslint-disable-next-line monorepo/no-internal-import
import {QueryObserverResult} from '@aboutyou/data-fetcher';
import React, {FC, createContext, useContext, useMemo} from 'react';

interface CtxValue<T> {
  data?: T;
  error?: unknown;
  isError?: boolean;
  isIdle?: boolean;
  isLoading?: boolean;
  updateCache: (data: T) => void;
  refetch: () => Promise<QueryObserverResult<T>> | void;
}

interface Config<T> {
  defaultValue: T | undefined;
  useQueryHook: () => CtxValue<T>;
}

export const createDataStore = <T extends unknown>({
  defaultValue,
  useQueryHook,
}: Config<T>) => {
  const Context = createContext<CtxValue<T>>({
    data: defaultValue,
    error: undefined,
    isError: false,
    isIdle: false,
    isLoading: false,
    updateCache: () => void 0,
    refetch: () => void 0,
  });

  const useData = () => useContext(Context);

  const Provider: FC<React.PropsWithChildren> = ({children}) => {
    const request = useQueryHook();
    const value = useMemo(
      () => ({
        data: request.data || defaultValue,
        error: request.error,
        isError: request.isError,
        isIdle: request.isIdle,
        isLoading: request.isLoading,
        updateCache: request.updateCache,
        refetch: request.refetch,
      }),
      [
        request.data,
        request.isLoading,
        request.error,
        request.isError,
        request.isIdle,
        request.updateCache,
        request.refetch,
      ],
    );
    return <Context.Provider value={value}>{children}</Context.Provider>;
  };

  return {
    useData,
    Provider,
  };
};
